import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toDateTimeInputValue } from '../../../library/dateTimeHelper';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { IPatientPurchase } from '../../../library/models/patientPurchase';
import ResourcesIds from '../../../resources/ResourcesIds';
import { AppDateTimePicker } from '../../pickers/AppDateTimePicker';
import { createRecordSessionSchema, RecordSessionFormValues } from './recordSessionSchema';
import { useIsCompact } from '../../../library/useIsCompact';
import {
    descriptionStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    formAlertStyles,
    serviceLineStyles,
} from './recordSessionDialog.styles';
import { localised } from '../../../library/models/localisedText';

interface RecordSessionDialogProps {
    open: boolean;
    purchase: IPatientPurchase;
    isSaving: boolean;
    onClose: () => void;
    /** The parent owns the mutation, because the card has to refresh on its own account. */
    onConfirm: (startedAt: string) => void;
}

/**
 * Asks when the session started before recording it.
 *
 * A session is a day *and* a clock time -- "21 January, 13:00" -- because a clinic that
 * only knows which day somebody was treated cannot answer the two questions it is actually
 * asked: what happened at three o'clock, and was this patient here before or after that
 * one. How long it ran is not asked for: that is the service's own duration, snapshotted
 * onto the purchase when it was sold, and typing it a second time could only disagree
 * with it.
 *
 * It opens on the current moment, so the common case -- pressing the button as the patient
 * leaves -- is still one click past this dialog. The field is editable for the other case,
 * a therapist typing up yesterday afternoon.
 */
export const RecordSessionDialog = (props: RecordSessionDialogProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createRecordSessionSchema(translate), [translate]);

    const {
        register, control, handleSubmit, reset, formState: { errors, isValid },
    } = useValidatedForm<RecordSessionFormValues>(schema, { defaultValues: { startedAt: '' } });

    // Reopened on the moment it is reopened, never on the moment the card first rendered.
    useEffect(() => {
        if (props.open) {
            reset({ startedAt: toDateTimeInputValue(new Date()) });
        }
    }, [props.open, reset]);

    const onValid = (values: RecordSessionFormValues): void => props.onConfirm(values.startedAt);

    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="xs" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.PURCHASE_RECORD_SESSION_TITLE)}
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" sx={descriptionStyles}>
                    {translate(ResourcesIds.PURCHASE_RECORD_SESSION_SUBTITLE)}
                </Typography>

                <Controller
                    name="startedAt"
                    control={control}
                    render={({ field }) => (
                        <AppDateTimePicker
                            label={translate(ResourcesIds.PURCHASE_RECORD_SESSION_STARTED_AT_LABEL)}
                            timeLabel={translate(ResourcesIds.PICKER_TIME_LABEL)}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            inputRef={field.ref}
                            // A session that has not happened yet cannot be recorded as held.
                            // The editable case is a therapist typing up yesterday afternoon,
                            // which this leaves alone.
                            disableFuture
                            {...fieldProps(errors, 'startedAt')}
                        />
                    )}
                />

                <Typography variant="body2" sx={serviceLineStyles}>
                    {localised(props.purchase.serviceNameRo, props.purchase.serviceNameEn, i18n.language)}
                    {' — '}
                    {translate(ResourcesIds.PURCHASE_SESSION_DURATION, {
                        minutes: props.purchase.durationMinutes,
                    })}
                </Typography>
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={props.onClose} variant="outlined" disabled={props.isSaving}>
                    {translate(ResourcesIds.CANCEL_TEXT)}
                </Button>
                <Button
                    onClick={handleSubmit(onValid)}
                    variant="contained"
                    disabled={!isValid || props.isSaving}
                >
                    {translate(ResourcesIds.PURCHASE_RECORD_SESSION_SUBMIT)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
